import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useConsent } from "@/components/ConsentContext";

export default function CookieConsent() {
  const {
    consent,
    settingsOpen,
    hasChoice,
    acceptAll,
    acceptNecessary,
    saveConsent,
    openSettings,
    closeSettings,
  } = useConsent();
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [externalMedia, setExternalMedia] = useState(false);

  useEffect(() => {
    setStatistics(consent?.statistics === true);
    setMarketing(consent?.marketing === true);
    setExternalMedia(consent?.externalMedia === true);
  }, [consent, settingsOpen]);

  return (
    <>
      {!hasChoice && (
        <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-zinc-200 bg-white shadow-[0_-18px_42px_rgba(0,0,0,0.16)]">
          <div className="container mx-auto flex flex-col gap-5 px-4 py-5 md:flex-row md:items-center md:justify-between md:px-6">
            <div className="max-w-3xl">
              <p className="text-base font-bold text-zinc-950">Datenschutzeinstellungen</p>
              <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                Wir verwenden notwendige Funktionen sowie, mit Ihrer Zustimmung, Google Analytics zur Reichweitenmessung
                und Google Maps zur Anzeige unseres Standorts.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button onClick={acceptAll}>Alle akzeptieren</Button>
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/5" onClick={openSettings}>
                Einstellungen
              </Button>
              <Button variant="outline" className="border-zinc-300" onClick={acceptNecessary}>
                Nur notwendige
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={(open) => (open ? openSettings() : closeSettings())}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Datenschutzeinstellungen</DialogTitle>
            <DialogDescription>
              Legen Sie fest, welche Dienste zusätzlich zu den notwendigen Funktionen geladen werden dürfen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <ConsentRow
              title="Notwendig"
              description="Erforderlich für grundlegende Funktionen der Website. Diese Kategorie ist immer aktiv."
              checked
              disabled
            />
            <ConsentRow
              title="Statistik"
              description="Google Analytics hilft uns zu verstehen, wie die Website genutzt wird."
              checked={statistics}
              onCheckedChange={setStatistics}
            />
            <ConsentRow
              title="Marketing"
              description="Ermöglicht die Messung von Google-Ads-Conversions, einschließlich Anrufen nach einem Anzeigenklick. Dabei können werbebezogene Cookies und vergleichbare Technologien eingesetzt werden."
              checked={marketing}
              onCheckedChange={setMarketing}
            />
            <ConsentRow
              title="Externe Medien"
              description="Erlaubt das Laden von Google Maps zur Anzeige unseres Standorts."
              checked={externalMedia}
              onCheckedChange={setExternalMedia}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="border-zinc-300" onClick={acceptNecessary}>
              Nur notwendige
            </Button>
            <Button
              variant="outline"
              className="border-primary text-primary hover:bg-primary/5"
              onClick={() => saveConsent({ statistics, marketing, externalMedia })}
            >
              Auswahl speichern
            </Button>
            <Button onClick={acceptAll}>Alle akzeptieren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type ConsentRowProps = {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

function ConsentRow({ title, description, checked, disabled, onCheckedChange }: ConsentRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-4">
      <div>
        <p className="font-semibold text-zinc-950">{title}</p>
        <p className="mt-1 text-sm leading-relaxed text-zinc-600">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} aria-label={title} />
    </div>
  );
}
